import React from 'react';
import { THEME, SHADOWS, RADII, CARD_STYLE, SECTION_HEADER, GRADIENTS } from './naTheme';

// ─── Section Card ────────────────────────────────────────────────────────────
// Wraps any content in a premium rounded card with an optional gradient header.
export const SectionCard = ({ title, iconClass, children, headerBg, headerExtra, bodyPadding = '20px' }) => (
  <div style={CARD_STYLE}>
    {title && (
      <div style={SECTION_HEADER(headerBg)}>
        {iconClass && <i className={iconClass} style={{ fontSize: '16px', opacity: 0.95 }} />}
        <span style={{ fontWeight: 600, fontSize: '15px', letterSpacing: '0.3px', flex: 1 }}>
          {title}
        </span>
        {headerExtra}
      </div>
    )}
    <div style={{ padding: bodyPadding }}>
      {children}
    </div>
  </div>
);

// ─── Stat Row ─────────────────────────────────────────────────────────────────
// A single label/value row used inside cards.
export const StatRow = ({ label, value, color, last = false }) => (
  <div style={{
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    padding:        '10px 0',
    borderBottom:   last ? 'none' : '1px solid #F3F4F6',
  }}>
    <span style={{ color: THEME.textSecondary, fontSize: '13px' }}>{label}</span>
    <span style={{ color: color || THEME.textPrimary, fontWeight: 700, fontSize: '14px' }}>{value}</span>
  </div>
);

// ─── Gradient Button ──────────────────────────────────────────────────────────
export const GradientButton = ({ children, onClick, disabled = false, style = {}, size = 'md' }) => {
  const pad = { sm: '6px 14px', md: '9px 20px', lg: '12px 28px' };
  const fz  = { sm: '12px',     md: '13px',     lg: '15px'      };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="na-gradient-btn"
      style={{
        background:   disabled ? '#D1D5DB' : THEME.gradient,
        color:        '#fff',
        border:       'none',
        borderRadius: RADII.button,
        padding:      pad[size],
        fontSize:     fz[size],
        fontWeight:   600,
        cursor:       disabled ? 'not-allowed' : 'pointer',
        boxShadow:    disabled ? 'none' : SHADOWS.button,
        transition:   'all 0.2s ease',
        display:      'inline-flex',
        alignItems:   'center',
        gap:          '6px',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

// ─── Outlined Button ──────────────────────────────────────────────────────────
export const OutlinedButton = ({ children, onClick, style = {} }) => (
  <button
    onClick={onClick}
    className="na-outlined-btn"
    style={{
      background:   'transparent',
      color:        THEME.primary,
      border:       `2px solid ${THEME.primary}`,
      borderRadius: RADII.button,
      padding:      '7px 18px',
      fontSize:     '13px',
      fontWeight:   600,
      cursor:       'pointer',
      transition:   'all 0.2s ease',
      display:      'inline-flex',
      alignItems:   'center',
      gap:          '6px',
      ...style,
    }}
  >
    {children}
  </button>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
export const StatusBadge = ({ status }) => {
  let bg, color, text, iconClass;
  if (status === null || status === undefined) {
    bg = THEME.warningBg; color = '#B45309'; text = 'Pending';  iconClass = 'fas fa-clock';
  } else if (status === '1' || status === 1) {
    bg = THEME.successBg; color = '#047857'; text = 'Approved'; iconClass = 'fas fa-check-circle';
  } else {
    bg = THEME.dangerBg;  color = '#B91C1C'; text = 'Declined'; iconClass = 'fas fa-times-circle';
  }
  return (
    <span style={{
      background:   bg,
      color,
      padding:      '4px 12px',
      borderRadius: RADII.chip,
      fontSize:     '12px',
      fontWeight:   600,
      display:      'inline-flex',
      alignItems:   'center',
      gap:          '5px',
      whiteSpace:   'nowrap',
    }}>
      <i className={iconClass} style={{ fontSize: '11px' }} />
      {text}
    </span>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
export const EmptyState = ({ message = 'No data available', iconClass = 'fas fa-inbox' }) => (
  <div style={{ textAlign: 'center', padding: '36px 20px', color: THEME.textSecondary }}>
    <i className={iconClass} style={{ fontSize: '36px', marginBottom: '12px', display: 'block', color: '#D1D5DB' }} />
    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{message}</p>
  </div>
);

// ─── Summary Stat Box ─────────────────────────────────────────────────────────
// Colored gradient mini-card for summary numbers.
export const SummaryStatBox = ({ iconClass, label, value, gradient, style = {} }) => (
  <div style={{
    borderRadius: RADII.inner,
    padding:      '18px',
    textAlign:    'center',
    background:   `linear-gradient(135deg, ${gradient})`,
    color:        '#fff',
    marginBottom: '16px',
    ...style,
  }}>
    {iconClass && (
      <i className={iconClass} style={{ fontSize: '22px', opacity: 0.85, marginBottom: '8px', display: 'block' }} />
    )}
    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.7px', opacity: 0.85 }}>{label}</div>
    <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px' }}>{value}</div>
  </div>
);

// ─── Attendance Stat Chip ─────────────────────────────────────────────────────
export const AttendanceStat = ({ label, value, bg, color }) => (
  <div style={{
    flex:          1,
    borderRadius:  '10px',
    padding:       '12px 8px',
    textAlign:     'center',
    background:    bg,
  }}>
    <div style={{ fontSize: '20px', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '10px', color, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '3px', opacity: 0.8 }}>
      {label}
    </div>
  </div>
);

// ─── Fee Table ────────────────────────────────────────────────────────────────
export const FeeTable = ({ data, columns }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#F9FAFB' }}>
          {columns.map((col, i) => (
            <th key={i} style={{
              padding:       '11px 16px',
              fontSize:      '11px',
              fontWeight:    600,
              color:         THEME.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign:     i === 0 ? 'left' : 'center',
              borderBottom:  '2px solid #E5E7EB',
              whiteSpace:    'nowrap',
            }}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => (
          <tr
            key={i}
            className="na-fee-table-row"
            style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}
          >
            {columns.map((col, j) => (
              <td key={j} style={{
                padding:      '11px 16px',
                fontSize:     '13px',
                color:        col.colorFn ? col.colorFn(row) : THEME.textPrimary,
                fontWeight:   col.bold ? 600 : 400,
                textAlign:    j === 0 ? 'left' : 'center',
                borderBottom: '1px solid #F3F4F6',
              }}>
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── View Details Link ────────────────────────────────────────────────────────
export const ViewDetailsLink = ({ onClick }) => (
  <div style={{ textAlign: 'center', paddingTop: '10px', borderTop: '1px solid #F3F4F6' }}>
    <span
      onClick={onClick}
      style={{ color: THEME.primary, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
    >
      View Details <i className="fas fa-arrow-right" style={{ fontSize: '11px' }} />
    </span>
  </div>
);
